import React, { useState, useMemo, useEffect } from 'react';
import { Station, Project } from '../../types';
import { MatrixType } from '../../lib/url/matrixMapping';
import { downloadMatrix } from '../../lib/export/files';
import { getEffectivePopulation } from '../../utils/population';
import { formatCurrency, formatCurrencyNoDecimals } from '../../lib/format/currency';
import { matrixToView, viewToMatrix, getDefaultMatrixType, MATRIX_DEFINITIONS } from '../../lib/url/matrixMapping';
import { getSortedStations, getSortedStationIndices, reorderMatrix } from '../../lib/matrix/ordering';

interface Props {
  data: number[][] | Station[];
  stations: Station[];
  type: MatrixType;
  project: Project;
  onStationUpdate: (station: Station) => void;
  onDownload: (fn: () => void) => void;
  initialView?: string | null;
}

export const MatrixTable: React.FC<Props> = ({
  data,
  stations,
  type,
  project,
  onStationUpdate,
  onDownload,
  initialView
}) => {
  const [editingCell, setEditingCell] = useState<{ stationId: string; value: string } | null>(null);
  const [hoveredCell, setHoveredCell] = useState<{ row: number | null; col: number | null }>({ row: null, col: null });
  const [selectedView, setSelectedView] = useState<string | null>(initialView || null);

  // Sync selectedView with type prop
  useEffect(() => {
    setSelectedView(type);
  }, [type]);

  // Use stations from matrices if available, otherwise use network stations
  const displayStations = MATRIX_DEFINITIONS[type].type === 'cities' ? (data as Station[]) : stations;

  const sortedStations = useMemo(() => {
    if (MATRIX_DEFINITIONS[type].type === 'cities') {
      return getSortedStations(data as Station[]);
    }
    return getSortedStations(stations);
  }, [data, stations, type]);

  const sortedData = useMemo(() => {
    if (MATRIX_DEFINITIONS[type].type === 'cities') {
      return sortedStations;
    }
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    const sortedIndices = getSortedStationIndices(stations);
    const isValidMatrix = data.every(row => Array.isArray(row) && row.length === data.length);
    if (!isValidMatrix) {
      console.warn('Invalid matrix structure detected');
      return [];
    }
    return reorderMatrix(data as number[][], sortedIndices);
  }, [data, stations, type, sortedStations]);
  
  const formatValue = (value: number): string => {
    if (MATRIX_DEFINITIONS[type].hasCurrency) {
      return formatCurrencyNoDecimals(value, project.config.bond.currency);
    }

    switch (type) {
      case 'gravity':
        return value === 0 ? '0' : value.toFixed(4);
      case 'constructionCostPerTicket':
        return value === 0 ? '0%' : `${(value * 100).toFixed(1)}%`;
      case 'trainEmissions':
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
      case 'flyingEmissions':
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
      case 'drivingEmissions':
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
      case 'fares':
      case 'businessFares':
      case 'economyFares':
      case 'discountFares':
      case 'revenue':
      case 'constructionCost': {
        return formatCurrencyNoDecimals(value, project.config.bond.currency);
      }
      case 'travelTime':
      case 'flightTime':
      case 'drivingTime': {
        if (value === 0) return '0:00';
        const days = Math.floor(value);
        const hours = Math.floor((value - days) * 24);
        const minutes = Math.round(((value - days) * 24 - hours) * 60);
        if (days > 0) {
          return `${days}:${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
        return `${hours}:${minutes.toString().padStart(2, '0')}`;
      }
      default:
        return new Intl.NumberFormat('en-US').format(Math.round(value));
    }
  };

  const handleMouseEnter = (row: number | null, col: number | null) => {
    setHoveredCell({ row, col });
  };

  const handleMouseLeave = () => {
    setHoveredCell({ row: null, col: null });
  };

  const handlePopulationChange = (station: Station, value: string) => {
    if (!onStationUpdate) return;
    
    // Remove commas and convert to number
    const numValue = parseInt(value.replace(/,/g, ''));
    if (isNaN(numValue) || numValue < 0) return;

    onStationUpdate({
      ...station,
      population: numValue
    });
  };

  const handlePopulationBlur = (station: Station) => {
    setEditingCell(null);
    const value = editingCell?.value;
    if (value && editingCell?.stationId === station.id) {
      handlePopulationChange(station, value);
    }
  };

  const handlePopulationFocus = (station: Station) => {
    setEditingCell({ stationId: station.id, value: station.population.toString() });
  };

  const handleInputChange = (station: Station, value: string) => {
    // Only allow numbers and commas
    if (!/^[\d,]*$/.test(value)) return;
    
    setEditingCell({ stationId: station.id, value });
  };

  const downloadFn = useMemo(() => {
    return () => {
      downloadMatrix(type, data, project);
    };
  }, [type, data, project]);

  useEffect(() => {
    onDownload(() => {
      downloadMatrix(type, data, project);
    });
  }, [data, type, project, onDownload]);

  const isMatrixData = (data: number[][] | Station[]): data is number[][] => {
    return MATRIX_DEFINITIONS[type].type !== 'cities';
  };

  // Add a check to prevent rendering if data is not ready
  if (!sortedData || !sortedStations) {
    return <div>Loading matrix data...</div>;
  }

  if (type === 'cities') {
    return (
      <div className="overflow-auto relative pb-24">
        <table className="matrix-table">
          <thead className="sticky top-0 bg-[#F5F2EB]">
            <tr>
              <th className="sticky-col bg-[#F5F2EB] p-4 text-[#4A4538] font-mono text-sm">
                City
              </th>
              <th className="p-4 text-[#4A4538] font-mono text-sm group relative">
                <div className="flex items-center justify-end gap-1 text-right">
                  <span>Population within 30km</span>
                </div>
              </th>
              <th className="p-4 text-[#4A4538] font-mono text-sm group relative">
                <div className="flex items-center justify-end gap-1 text-right">
                  <span>Arrivals</span>
                </div>
              </th>
              <th className="p-4 text-[#4A4538] font-mono text-sm group relative">
                <div className="flex items-center justify-end gap-1 text-right">
                  <span>Revenue</span>
                </div>
              </th>
              <th className="p-4 text-[#4A4538] font-mono text-sm group relative">
                <div className="flex items-center justify-end gap-1 text-right">
                  <span>Share of Total Revenue</span>
                </div>
              </th>
              <th className="p-4 text-[#4A4538] font-mono text-sm group relative">
                <div className="flex items-center justify-end gap-1 text-right">
                  <span>Centrality</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedStations.map((station, i) => (
              <tr 
                key={station.id}
                className={hoveredCell.row === i ? 'bg-[#E5E2DB]' : ''}
                onMouseEnter={() => handleMouseEnter(i, null)}
                onMouseLeave={handleMouseLeave}
              >
                <th className="sticky-col p-4 text-[#4A4538] font-mono text-sm">
                  {station.name}
                </th>
                <td className="p-4 text-[#4A4538] font-mono text-sm text-right">
                  {new Intl.NumberFormat('en-US').format(getEffectivePopulation(station))}
                </td>
                <td className="p-4 text-[#4A4538] font-mono text-sm text-right">
                  {new Intl.NumberFormat('en-US').format(station.annualArrivals || 0)}
                </td>
                <td className="p-4 text-[#4A4538] font-mono text-sm text-right">
                  {formatCurrencyNoDecimals(station.annualRevenue || 0, project.config.bond.currency)}
                </td>
                <td className="p-4 text-[#4A4538] font-mono text-sm text-right">
                  {(station.revenuePercentage || 0).toFixed(1)}%
                </td>
                <td className="p-4 text-[#4A4538] font-mono text-sm text-right">
                  {Math.round(station.centrality || 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-auto relative h-[calc(100vh-180px)] pb-24">
      <table className="matrix-table">
        <thead>
          <tr>
            <th className={`sticky-col bg-[#F5F2EB] p-4 text-[#4A4538] font-mono text-sm`}>
              City
            </th>
            {sortedStations.map((station, j) => (
              <th 
                key={station.id}
                className={`p-4 text-[#4A4538] font-mono text-sm ${hoveredCell.col === j ? 'bg-[#E5E2DB]' : ''}`}
                onMouseEnter={() => handleMouseEnter(null, j)}
                onMouseLeave={handleMouseLeave}
              >
                {station.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedStations.map((station, i) => (
            <tr 
              key={station.id} 
              className={hoveredCell.row === i ? 'bg-[#E5E2DB]' : ''}
            >
              <th 
                className={`sticky-col p-4 text-[#4A4538] font-mono text-sm ${hoveredCell.row === i ? 'bg-[#E5E2DB]' : ''}`}
                onMouseEnter={() => handleMouseEnter(i, null)}
                onMouseLeave={handleMouseLeave}
              >
                {station.name}
              </th>
              {sortedStations.map((colStation, j) => {
                const value = isMatrixData(sortedData) && sortedData[i] && sortedData[i][j] !== undefined 
                  ? sortedData[i][j] 
                  : 0;
                const isHighlighted = hoveredCell.col === j;
                return (
                  <td 
                    key={colStation.id}
                    className={`p-4 text-[#4A4538] font-mono text-sm text-right ${isHighlighted ? 'bg-[#E5E2DB]' : ''} ${value === 0 ? 'text-gray-400' : ''}`}
                    onMouseEnter={() => handleMouseEnter(i, j)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {formatValue(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}; 